import lodash, { cloneDeep, get, isEqual, maxBy, omit, set } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";

import { hasValueInKeys } from "@/react/collections";
import extractIndexFromPath from "@/react/components/table/extractIndexFromPath";
import { validateWithContext } from "@/react/schema/schemaFields";

const DEFAULT_OPTIONS = Object.freeze({
  // When the table data changes via the *setData* function, existing mutations are forgotten by default.
  // This is logical, but there are some cases where we may not want to do that.
  // This flag controls the behavior.
  clearMutationsOnDataChange: true,

  /**
   * TODO: ignoreValidationErrors *should* be set to FALSE by default.
   *   It is set to TRUE for now since each tool may have incompatibilities that need to be solved individually.
   *   This way we can turn on validation for some tables without breaking others.
   **/
  ignoreValidationErrors: true,

  // Collection of query functions and possibly a factory to create more
  queries: undefined,

  // Function to generate pre-validation data structures,
  // for use when validating the table's data using a schema
  validationDataInitializer: undefined,
});

/**
 * Track updates to table data, used internally and exposed in `useTable`
 *
 * @param {Array<*>} initialData
 * @param {string} idKey
 * @param {object} tableOptions
 *
 * @typedef {{data: Array<*>, rowsToCreate: Map<string, *>, rowsToUpdate: Map<string, *>, rowsToDelete: Map<string, *>, updateRows: function, addRows: function, deleteRows: function, resetData: function, bulkUpdateRows: function, setData: function, setRowsToCreate: function, setRowsToUpdate: function, setRowsToDelete: function, hasErrors: boolean}} TableMutations
 *
 * @returns {TableMutations}
 */
const useTableMutations = (initialData, idKey, tableOptions) => {
  const options = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...tableOptions }),
    [tableOptions]
  );

  const initialDataClone = useMemo(() => {
    const clone = cloneDeep(initialData || []);
    return clone.map(appendKeyAndErrors(idKey));
  }, [initialData]);

  const [data, setData] = useState(initialDataClone);

  const hasErrors = useMemo(() => data?.some(isInvalidRow), [data]);

  const [rowsToCreate, setRowsToCreate] = useState(new Map());
  const [rowsToUpdate, setRowsToUpdate] = useState(new Map());
  const [rowsToDelete, setRowsToDelete] = useState(new Map());

  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    resetData(options?.clearMutationsOnDataChange);
  }, [initialDataClone]);

  const queryFunctions = useMemo(
    () =>
      tableOptions?.queries
        ? {
            ...omit(tableOptions.queries, ["factory"]),
            ...tableOptions.queries?.factory(data),
          }
        : {},
    [data, tableOptions]
  );

  const validateRows = useCallback(
    async (idKey, schema, rows, allRows) => {
      if (options?.ignoreValidationErrors) {
        return rows.map(appendKeyAndErrors(idKey));
      }
      return await validateAllRows({
        idKey,
        schema,
        newRows: rows,
        allRows,
        queryFunctions,
        validationDataInitializer: options?.validationDataInitializer,
      });
    },
    [options, queryFunctions]
  );

  const validateAndSyncOnUpdate = useCallback(
    async (updatedDataMap, rowsToUpdateMap, schema) => {
      const validatedRows = await validateRows(
        idKey,
        schema,
        Array.from(updatedDataMap.values())
      );
      validatedRows.forEach((row) => {
        updatedDataMap.set(row[KEY], row);
      });

      const newRowsToUpdate = new Map(rowsToUpdate);
      const newRowsToCreate = new Map(rowsToCreate);
      rowsToUpdateMap.forEach((patchValue, rowKey) => {
        const row = updatedDataMap.get(rowKey);
        if (rowsToCreate.has(rowKey)) {
          newRowsToCreate.set(rowKey, row);
          return;
        }

        const newRowToUpdate = newRowsToUpdate.has(rowKey)
          ? newRowsToUpdate.get(rowKey)
          : { [idKey]: lodash.get(row, idKey) };
        patchValue.forEach((value, columnAccessor) => {
          set(newRowToUpdate, columnAccessor, value);
        });
        newRowsToUpdate.set(rowKey, newRowToUpdate);
      });

      setData(Array.from(updatedDataMap.values()));
      setRowsToUpdate(newRowsToUpdate);
      setRowsToCreate(newRowsToCreate);
    },
    [
      data,
      setData,
      rowsToCreate,
      setRowsToCreate,
      rowsToUpdate,
      setRowsToUpdate,
    ]
  );

  const bulkUpdateRows = useCallback(
    /**
     * @param {String} identifierColumn
     * @param {Map<string, Map<string, any>>} rowsToUpdateMap
     * @param {ObjectSchema} schema
     * @returns {Promise<void>}
     */
    async (identifierColumn, rowsToUpdateMap, schema) => {
      setIsProcessing(true);
      const updatedDataMap = new Map(
        data.map((row) => {
          const rowKey = row[KEY];
          const identifierKey = get(row, identifierColumn);
          // Skip rows not meant to be updated
          if (!rowsToUpdateMap.has(identifierKey)) {
            return [rowKey, row];
          }
          const patchValue = rowsToUpdateMap.get(identifierKey);
          // Re-map to the actual row key, not the identifier
          rowsToUpdateMap.set(rowKey, patchValue);
          rowsToUpdateMap.delete(identifierKey);

          const newRow = cloneDeep(row);
          patchValue.forEach((value, columnAccessor) => {
            set(newRow, columnAccessor, value);
          });

          // If the row hasn't actually changed, then remove it from being updated
          if (isEqual(newRow, row)) {
            rowsToUpdateMap.delete(rowKey);
          }

          return [rowKey, newRow];
        })
      );

      // Remove any rows that don't actually exist in the current data, and are thus not an update
      for (const key of rowsToUpdateMap.keys()) {
        if (!updatedDataMap.has(key)) {
          rowsToUpdateMap.delete(key);
        }
      }

      // Only validate and sync if there is something to update
      if (rowsToUpdateMap.size > 0) {
        await validateAndSyncOnUpdate(updatedDataMap, rowsToUpdateMap, schema);
      }
      setIsProcessing(false);
    },
    [
      data,
      setData,
      rowsToCreate,
      setRowsToCreate,
      rowsToUpdate,
      setRowsToUpdate,
      setIsProcessing,
    ]
  );

  const updateRows = useCallback(
    /**
     * @param {Array<string>} rowKeys
     * @param {Map<string, any>} patchValue columnAccessor -> value
     * @param {ObjectSchema} schema
     * @returns {Promise<void>}
     */
    async (rowKeys, patchValue, schema) => {
      const updatedDataMap = new Map(
        data.map((row) => {
          const rowKey = row[KEY];
          // Skip rows not meant to be updated
          if (!rowKeys.includes(rowKey)) {
            return [rowKey, row];
          }

          const newRow = cloneDeep(row);
          patchValue.forEach((value, columnAccessor) => {
            set(newRow, columnAccessor, value);
          });

          // If the row hasn't actually changed, then remove it from being updated
          if (isEqual(newRow, row)) {
            rowKeys.splice(rowKeys.indexOf(rowKey), 1);
          }

          return [rowKey, newRow];
        })
      );

      const rowsToUpdateMap = new Map(rowKeys.map((key) => [key, patchValue]));

      // Only validate and sync if there is something to update
      if (rowsToUpdateMap.size > 0) {
        await validateAndSyncOnUpdate(updatedDataMap, rowsToUpdateMap, schema);
      }
    },
    [
      data,
      setData,
      rowsToCreate,
      setRowsToCreate,
      rowsToUpdate,
      setRowsToUpdate,
      setIsProcessing,
    ]
  );

  const addRows = useCallback(
    /**
     *
     * @param {Array<any>} rowsToAdd
     * @param {ObjectSchema} schema
     */
    async (rowsToAdd, schema) => {
      setIsProcessing(true);
      const oldRows = [...data];
      const startKey = guessNewKey(oldRows);
      const newRows = rowsToAdd.map((row, i) => {
        row[KEY] = startKey + i;
        return row;
      });
      const newAndOldRows = newRows.concat(oldRows);
      const validatedRows = await validateRows(
        idKey,
        schema,
        newRows,
        newAndOldRows
      );

      setRowsToCreate((oldRowsToCreate) => {
        const newRowsToCreate = new Map(oldRowsToCreate);
        validatedRows.forEach((row) => {
          const rowToCreate = cloneDeep(row);
          delete rowToCreate[KEY];
          delete rowToCreate[ERRORS];
          newRowsToCreate.set(row[KEY], rowToCreate);
        });
        return newRowsToCreate;
      });
      setData(validatedRows.concat(oldRows));
      setIsProcessing(false);
      return newRows;
    },
    [data, setData, setRowsToCreate, setIsProcessing]
  );

  const deleteRows = useCallback(
    /**
     * @param {Array<string>} rowKeys
     */
    (rowKeys) => {
      rowKeys.forEach((rowKey) => {
        // Is this row newly added?
        if (rowsToCreate.has(rowKey)) {
          setRowsToCreate((old) => {
            const newRowsToCreate = new Map(old);
            newRowsToCreate.delete(rowKey);
            return newRowsToCreate;
          });
        } else {
          setRowsToDelete((old) => {
            const newRowsToDelete = new Map(old);
            const targetedRow = (data ?? []).find(
              (row) => row?.[KEY] === rowKey
            );
            newRowsToDelete.set(rowKey, targetedRow);
            return newRowsToDelete;
          });
        }
      });
      setData((old) => old.filter((r) => !rowKeys.includes(r[KEY])));
    },
    [data, setData, rowsToCreate, setRowsToCreate, setRowsToDelete]
  );

  const resetData = useCallback(
    (clearMutations = true) => {
      setData(initialDataClone);
      if (clearMutations) {
        setRowsToCreate(new Map());
        setRowsToUpdate(new Map());
        setRowsToDelete(new Map());
      }
    },
    [
      initialDataClone,
      setData,
      setRowsToCreate,
      setRowsToUpdate,
      setRowsToDelete,
    ]
  );

  return {
    // Array of rows to display in table, each row is an object of column names to the values in those columns
    data,
    // Map of row keys to the row data which will be used to create the new row
    rowsToCreate,
    // Map of row keys to an object being a subset of the row data, containing only the column/value pairs to change
    rowsToUpdate,
    // Map of row keys to 'true' for the rows to be deleted
    rowsToDelete,

    // Functions to call when we want to update, add, or remove rows, respectively.
    // These take what is in the rowsTo___ maps and use it to determine the next value for 'data'
    // The content in the maps is not cleared, but it can be modified
    updateRows,
    addRows,
    deleteRows,
    // Clears all rowsTo___ maps and sets the data back to what it was initially
    resetData,
    // Similar to updateRows, but applies the change to the same column in all the given rows
    bulkUpdateRows,

    // Setters for the table data and the maps of keys to create/update/delete info
    setData,
    setRowsToCreate,
    setRowsToUpdate,
    setRowsToDelete,

    // Flag to indicate if any row in the table has its '_errors' key set to an object with some content
    hasErrors,
    // Flag to indicate we are in the midst of an add or update action
    isProcessing,
    // A collection of functions for running queries on the table data; by default, it is empty
    queryFunctions,
  };
};

export default useTableMutations;

export const KEY = "_key";
export const ERRORS = "_errors";

export const newRow = (columns) => {
  const row = appendKeyAndErrors(KEY)({});
  // TODO: Consider allowing defaults for any columns because
  //  it's possible to have uneditable cells which can change value
  columns
    .filter((c) => c.columnDef.editable || c.columnDef.importable)
    .forEach((column) => {
      set(row, column.id, column.columnDef.schema?.spec.default ?? null);
    });
  return row;
};

export const isInvalidRow = (row) =>
  hasValueInKeys(row[ERRORS] || {}, (r) => r.length > 0);

export const cellErrors = ({ row, column }) =>
  get(row?.original[ERRORS], column.id) || [];

/**
 * Use the table's key column as the id column.
 * This is required when the backend fails to create a row,
 * it will respond with the given id as the TAPResponseEntity.identifier.
 *
 * @param {Array<*>} rows
 * @param {string} idColumn
 * @returns {Array<*>}
 */
export const appendIdFromKey = (rows, idColumn) =>
  rows.map((row) => ({
    ...row,
    [idColumn]: row[KEY],
  }));

const appendKeyAndErrors = (key) => (row) => {
  row[KEY] = lodash.get(row, key) || row[KEY];
  row[ERRORS] = {};
  return row;
};

const guessNewKey = (data) => {
  const max = maxBy(data, (r) => r[KEY]);
  return get(max, KEY, 0) + 1;
};

export const validateAllRows = async ({
  idKey,
  schema,
  newRows,
  allRows = [],
  queryFunctions,
  validationDataInitializer,
}) => {
  try {
    const allDataRows = allRows.length ? allRows : newRows;
    const metaData = validationDataInitializer?.(allDataRows);
    await validateWithContext({
      schema: yup.array().of(schema),
      data: newRows,
      context: {
        allRows: allDataRows,
        // Used as a cache for grouping rows by column values - used for the unique validator
        columnMap: new Map(),
        queryFunctions: queryFunctions,
        metaData,
      },
    });
    return newRows.map(appendKeyAndErrors(idKey));
  } catch (e) {
    if (!(e instanceof yup.ValidationError)) {
      throw e;
    }

    const errorMap = new Map();
    for (const errorData of e.inner) {
      const path = errorData?.path;
      const [index, subpath] = extractIndexFromPath(path);
      const errorMessages = errorData?.errors;
      const errorContext = { path: subpath, details: errorMessages };

      if (!errorMap.has(index)) {
        errorMap.set(index, []);
      }
      errorMap.get(index).push(errorContext);
    }

    return newRows.map((row, index) => {
      // Reset previous errors and clone the row so we don't mutate the original
      row = cloneDeep(appendKeyAndErrors(idKey)(row));
      if (errorMap.has(index)) {
        const errors = errorMap.get(index);
        errors.forEach(({ path, details }) =>
          set(row, `${ERRORS}.${path}`, details)
        );
      }
      return row;
    });
  }
};
